import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, Check, Share2, Trash2 } from 'lucide-react';

import { ApiError } from '@/lib/api/errors';
import { queryKeys } from '@/lib/api/queryKeys';
import { xurveyApi, type StylePreset } from '@/lib/api/xurvey';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PromptDialog } from '@/components/ui/PromptDialog';
import { Select } from '@/components/ui/Input';
import { ApplyStyleDialog } from './ApplyStyleDialog';
import { useT } from '@/lib/i18n/useT';

/**
 * Los estilos guardados de la empresa, arriba del panel de diseño.
 *
 * Existe porque montar una activación empezaba siempre por rehacer a mano lo
 * mismo: la paleta de la marca, su logo, su fondo, la forma del formulario. Se
 * guarda una vez y se aplica en el siguiente evento.
 *
 * Va **fuera de las tres pestañas** —Look, Style y Form— porque un estilo las
 * cruza: lleva colores, tipografía y disposición. Dentro de una de ellas
 * parecería que sólo guarda esa parte.
 *
 * Se ven los de la familia (matriz, hermanas e hijas) y se distingue lo propio:
 * el `canEdit` lo decide el servidor, así que el botón de retirar sólo aparece
 * donde de verdad va a funcionar.
 */
export function StylePresetBar({
  apariencia,
  seleccionado: seleccionadoGuardado = null,
  modificado = false,
  onAplicar,
}: {
  /** Lo que se está viendo ahora, para poder guardarlo. */
  apariencia: { design: Record<string, unknown>; styleOverrides: Record<string, string> };
  /**
   * El estilo del que partió ESTE despliegue (`overrides.stylePresetId`).
   *
   * Viene del despliegue y no de un estado local: al salir y volver, el
   * selector tiene que seguir marcando de dónde se partió. Antes se perdía
   * al desmontar y la persona no sabía si había aplicado algo o no.
   */
  seleccionado?: number | null;
  /** Si la apariencia se tocó después de aplicarlo. Se dice, no se esconde. */
  modificado?: boolean;
  onAplicar: (estilo: StylePreset) => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  /** Nombrar el estilo y confirmar su retirada, con los diálogos del producto. */
  const [nombrando, setNombrando] = useState(false);
  const [aRetirar, setARetirar] = useState<StylePreset | null>(null);
  /** El que se está llevando a otros eventos. */
  const [aplicandoA, setAplicandoA] = useState<StylePreset | null>(null);
  // El elegido ES el guardado en el despliegue; aplicar lo cambia allí.
  const elegido = seleccionadoGuardado;
  const aplicado = seleccionadoGuardado;

  const presets = useQuery({
    queryKey: queryKeys.xurvey.stylePresets,
    queryFn: () => xurveyApi.listStylePresets(),
    staleTime: 60_000,
  });

  const guardar = useMutation({
    mutationFn: (name: string) =>
      xurveyApi.createStylePreset({
        name,
        design: apariencia.design,
        styleOverrides: apariencia.styleOverrides,
      }),
    onSuccess: (creado: StylePreset) => {
      setError(null);
      setNombrando(false);
      // Se marca el que acaba de nacer: con la lista visible, ver aparecer TU
      // estilo señalado es la confirmación de que se guardó. Antes el único
      // acuse de recibo era que un número subiera de 1 a 2.
      // Queda ELEGIDO en el desplegable: ver tu estilo recién puesto es la
      // confirmación de que se guardó. Antes el único acuse de recibo era que
      // un número subiera de 1 a 2 dentro de un enlace que nadie lee.
      // Se aplica el recién creado: así queda marcado en el despliegue y ver
      // tu estilo recién puesto es la confirmación de que se guardó.
      onAplicar(creado);
      void queryClient.invalidateQueries({ queryKey: queryKeys.xurvey.stylePresets });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : t('designJourney.presetSaveFailed')),
  });

  const retirar = useMutation({
    mutationFn: (id: number) => xurveyApi.removeStylePreset(id),
    onSuccess: () => {
      setError(null);
      setARetirar(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.xurvey.stylePresets });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : t('designJourney.presetDeleteFailed')),
  });

  const items = presets.data?.items ?? [];
  // Derivado y no guardado: si el estilo elegido se retira, la selección se
  // cae sola en vez de quedarse apuntando a un id que ya no existe.
  const seleccionado = items.find((p) => p.id === elegido) ?? null;

  return (
    <div className="flex flex-col gap-2 border-b border-[var(--color-border-default)] px-3 py-2.5">
      <div className="flex items-center gap-2">
        <Bookmark className="h-3.5 w-3.5 text-[var(--color-fg-muted)]" aria-hidden />
        <span className="flex-1 text-[11px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
          {t('designJourney.presets')}
        </span>
        <button
          type="button"
          onClick={() => {
            setError(null);
            setNombrando(true);
          }}
          disabled={guardar.isPending}
          className="rounded-md border border-[var(--color-border-default)] px-2 py-1 text-[11px] hover:border-[var(--color-accent)] disabled:opacity-50"
        >
          {guardar.isPending ? t('common.saving') : t('designJourney.presetSave')}
        </button>
      </div>

      {/*
        UN DESPLEGABLE, no una lista.
        
        Estuvo plegado detrás de un enlace («Show the 2 saved styles») y eso lo
        volvía invisible: al guardar no cambiaba nada a la vista. Pero
        desplegarlo entero tampoco vale — una empresa con veinte estilos
        acabaría con una columna de veinte filas empujando los mandos de
        diseño fuera de la pantalla, que es justo lo que el plegado evitaba.
        
        El desplegable resuelve las dos cosas a la vez: ocupa una fila, se ve
        siempre, y aguanta los que haga falta.
      */}
      {items.length === 0 ? (
        <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
          {presets.isLoading ? t('common.loading') : t('designJourney.presetsEmpty')}
        </p>
      ) : (
        <>
          {/*
            ELEGIR APLICA. No hay que confirmar después.
            
            Al pasar de lista a desplegable dejé la aplicación detrás de un
            botón, y con eso seleccionar un estilo no hacía nada: la previa
            seguía igual y parecía roto (reportado como «selecciono un diseño
            guardado y no se está aplicando»). En la lista anterior pulsar la
            fila YA aplicaba, y ése es el gesto que la gente tiene aprendido.
            
            En un `<select>` nativo abrir la lista no dispara nada: `onChange`
            sólo salta al elegir, que es un acto deliberado. Así que no hay
            riesgo de pisar el diseño por curiosear.
          */}
          <Select
            value={String(elegido ?? '')}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : null;
              const estilo = items.find((x) => x.id === id);
              if (estilo) onAplicar(estilo);
            }}
            className="h-8 text-[12px]"
            aria-label={t('designJourney.presets')}
          >
            <option value="">{t('designJourney.presetPick', { n: String(items.length) })}</option>
            {items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.canEdit ? '' : ` · ${t('designJourney.presetShared')}`}
              </option>
            ))}
          </Select>

          <div className="flex items-center gap-1.5">
            {/* Aplicado: el visto lo dice, sin un botón que repetir. */}
            {aplicado === elegido && elegido != null ? (
              <span className="flex flex-1 items-center gap-1 text-[11px] text-[var(--color-accent)]">
                <Check className="h-3 w-3" aria-hidden />
                {t('designJourney.presetApplied')}
                {modificado ? (
                  <span className="text-[var(--color-fg-muted)]"> · {t('designJourney.presetModified')}</span>
                ) : null}
              </span>
            ) : (
              <span className="flex-1" />
            )}

            {/* Aplicarlo donde NO estás: el motivo por el que un estilo existe. */}
            <button
              type="button"
              disabled={!seleccionado}
              onClick={() => setAplicandoA(seleccionado)}
              className="flex items-center gap-1 rounded-md border border-[var(--color-border-default)] px-2 py-1 text-[11px] hover:border-[var(--color-accent)] disabled:opacity-40"
            >
              <Share2 className="h-3 w-3" aria-hidden />
              {t('designJourney.presetApplyElsewhere')}
            </button>

            {seleccionado?.canEdit ? (
              <button
                type="button"
                onClick={() => setARetirar(seleccionado)}
                aria-label={t('designJourney.presetDelete')}
                className="shrink-0 rounded-md border border-[var(--color-border-default)] p-1 text-[var(--color-fg-muted)] hover:text-[var(--color-error)]"
              >
                <Trash2 className="h-3 w-3" aria-hidden />
              </button>
            ) : null}
          </div>
        </>
      )}

      {error ? (
        <p role="alert" className="text-[10px] text-[var(--color-danger-fg)]">
          {error}
        </p>
      ) : null}

      <PromptDialog
        open={nombrando}
        onOpenChange={setNombrando}
        title={t('designJourney.presetSave')}
        description={t('designJourney.presetNameHint')}
        placeholder={t('designJourney.presetNamePlaceholder')}
        submitLabel={t('common.save')}
        loading={guardar.isPending}
        onSubmit={(nombre) => guardar.mutate(nombre)}
      />

      <ApplyStyleDialog preset={aplicandoA} onClose={() => setAplicandoA(null)} />

      <ConfirmDialog
        open={aRetirar != null}
        onOpenChange={(v) => !v && setARetirar(null)}
        tone="danger"
        title={t('designJourney.presetDelete')}
        description={
          aRetirar ? t('designJourney.presetDeleteConfirm', { name: aRetirar.name }) : ''
        }
        confirmLabel={t('designJourney.presetDelete')}
        loading={retirar.isPending}
        onConfirm={() => aRetirar && retirar.mutate(aRetirar.id)}
      />
    </div>
  );
}
